// src/style-comps/PropertyPage.styles.js
import styled from "styled-components";

// PageContainer 本身不再自己捲動；改成 flex column，把捲動範圍縮小到 ScrollArea
// （見下方），這樣 Header 跟 Tab 列表永遠固定在畫面上，不用 position:sticky，
// 也就不會有 sticky 元素在快速捲動時偶爾被底下內容穿透的瀏覽器渲染問題。
export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 20px 32px;
  box-sizing: border-box;
  overflow: hidden;
  font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
`;

/* ===== Header ===== */

export const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
`;

// 真正負責捲動的區域：Owner/Host Partner 卡片跟 Tab 內容都放這裡面，
// flex:1 + min-height:0 吃滿 PageContainer 扣掉 Header/Tabs 後剩下的高度。
export const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

export const SaveButton = styled.button`
  padding: 9px 22px;
  border-radius: 24px;
  border: none;
  background-color: #61cbc9;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 3px 8px rgba(97, 203, 201, 0.55);
  transition: all 0.2s ease;

  &:hover {
    background-color: #4fb8b5;
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 4px rgba(97, 203, 201, 0.6);
  }
`;

export const BackButton = styled.button`
  padding: 9px 22px;
  border-radius: 24px;
  border: 1px solid #61cbc9;
  background-color: #ffffff;
  color: #61cbc9;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e9f8f7;
  }

  &:active {
    transform: translateY(1px);
  }
`;

export const DiscardButton = styled.button`
  padding: 9px 22px;
  border-radius: 24px;
  border: 1px solid #e08a8a;
  background-color: #ffffff;
  color: #e08a8a;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #fdf0f0;
  }

  &:active {
    transform: translateY(1px);
  }
`;

export const ChangeCountText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #61cbc9;
  margin-right: 4px;
`;

/* ===== Tabs ===== */

// Tab 列表在 ScrollArea 之外，本來就不會被捲動捲走，不需要 position:sticky。
// align-self: flex-start 是必要的：PageContainer 現在是 flex-direction:column，
// 預設 align-items:stretch 會把子項目撐滿整個寬度，蓋掉 inline-flex 原本依內容
// 縮寬的效果，導致 Tabs 寬度跟 Tab 數量無關、永遠跟 PageContainer 一樣寬。
export const Tabs = styled.div`
  display: inline-flex;
  gap: 0;
  margin-bottom: 20px;
  border-radius: 999px;
  background-color: #ffffff;
  padding: 4px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
  align-self: flex-start;
`;

// Statement / Invoice / Inspection / Contract 尚未實作，鎖定不給點，
// 避免點進去看到跟其他 Tab 一樣的「coming soon」畫面，誤以為是 bug。
export const TabButton = styled.button`
  padding: 10px 26px;
  border-radius: 999px;
  border: none;
  background-color: ${({ active }) => (active ? "#61cbc9" : "transparent")};
  color: ${({ active, disabled }) => (disabled ? "#bbb" : active ? "#ffffff" : "#333")};
  font-size: 14px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ active, disabled }) =>
    disabled ? "transparent" : active ? "#61cbc9" : "rgba(97,203,201,0.08)"};
  }
`;

/* ===== Generic Card ===== */

export const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 18px 20px;
  margin-bottom: 24px;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.04);
`;

/* ===== Owner Card ===== */

export const OwnerCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: 18px;
`;

export const AvatarBox = styled.div`
  width: 65px;
  height: 65px;
  border-radius: 16px;
  background: #ffeccd;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;        
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;    
  }
`;


export const OwnerInfoWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const OwnerNameBlock = styled.div`
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 4px;
`;

export const OwnerInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  gap: 6px 28px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const OwnerRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

export const Label = styled.div`
  font-size: 12px;
  color: #777;
`;

export const Value = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #333;
`;

/* ===== Section Title (e.g. Children) ===== */

export const SectionTitle = styled.h3`
  margin: 22px 0 10px;
  font-size: 15px;
  font-weight: 600;
  color: #444;
`;

/* ===== Platform Section ===== */

export const PlatformSection = styled(Card)`
  margin-top: 6px;
`;

// Children 列表可能很長，欄位標題（KozyGuru Name / Platform Name / Link / Status /
// Listed / Booking Engine）凍結在 ScrollArea 頂部，滑動時不會捲走。
// ScrollArea 是唯一的捲動邊界（PageContainer 本身不再捲動），所以這裡的
// position:sticky 會正確參照 ScrollArea，不會再有之前參照到 PageContainer
// padding 造成的穿透瑕疵；will-change/transform 是保險，強制獨立合成層避免
// 快速往上滑時偶爾被底下 Parent Row 從背後穿透的瀏覽器渲染問題。
export const PlatformHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.8fr 108px 108px 130px;
  gap: 16px;
  padding: 0 4px 12px;
  margin-bottom: 4px;
  border-bottom: 1px solid #eee;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #999;
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 2;
  will-change: transform;
  transform: translateZ(0);
`;

export const PlatformHeaderCell = styled.div`
  &:nth-child(4),
  &:nth-child(5),
  &:nth-child(6) {
    text-align: center;
  }
`;

export const PlatformRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.8fr 108px 108px 130px;
  align-items: center;
  gap: 16px;
  padding: 14px 4px;
  border-top: 1px solid #f1f1f1;
  font-size: 12px;

  &:first-of-type {
    border-top: none;
  }
`;

/* Parent row: same grid as PlatformRow, visually promoted a level */
export const PlatformParentRow = styled(PlatformRow)`
  background: oklch(0.96 0.02 195);
  border: none;
  border-left: 3px solid #61cbc9;
  border-radius: 10px;
  padding: 14px 16px 14px 13px;
  margin-bottom: 6px;
`;

export const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
`;

export const ParentTag = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #61cbc9;
  background: #ffffff;
  border: 1px solid rgba(97, 203, 201, 0.4);
  padding: 2px 8px;
  border-radius: 999px;
`;

export const EmptyValue = styled.div`
  font-size: 12px;
  color: #bbb;
  text-align: center;
`;

export const PlatformInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid #61cbc9;
  font-size: 12px;
  background-color: #f9fefe;
  outline: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:focus {
    border-color: #61cbc9;
    box-shadow: 0 0 0 2px rgba(97, 203, 201, 0.35);
  }

  &:disabled {
    color: #aaa;
    background-color: #f5f5f5;
    border-color: #ddd;
    cursor: not-allowed;
  }
`;

const statusVariantColor = (variant) => {
  switch (variant) {
    case "listed":
      return "oklch(0.68 0.12 250)";
    case "booking":
      return "oklch(0.65 0.13 310)";
    default:
      return "#61cbc9";
  }
};

export const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-width: 92px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
  background-color: ${({ online, variant }) => (online ? statusVariantColor(variant) : "#e9e9e9")};
  color: ${({ online }) => (online ? "#ffffff" : "#666666")};

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.85;
  }

  &:hover {
    filter: brightness(0.96);
  }
`;

/* 自訂 checkbox，顏色跟 Status 一致 */

export const CheckBoxWrapper = styled.label`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  input {
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: 2px solid #61cbc9;
    background-color: #ffffff;
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  input:checked {
    background-color: #61cbc9;
    border-color: #61cbc9;
  }

  input:checked::after {
    content: "✓";
    position: absolute;
    top: -3px;
    left: 3px;
    font-size: 16px;
    color: #ffffff;
  }
`;

export const ErrorPopup = styled.div`
  position: fixed;
  top: 70px;
  right: -600px;
  transform: translateY(0%);
  background-color: rgba(97, 203, 201, 0.4);
  color: #333;
  padding: 12px 20px;
  border-radius: 8px;
  width: 80%;
  max-width: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: right 0.5s ease-in-out;
  ${({ show }) => show && `right: 10px;`}
`;

export const CloseButton = styled.button`
  background-color: transparent;
  color: #333;
  border: none;
  font-size: 24px;
  cursor: pointer;
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  font-weight: bold;
  &:hover {
    color: #61cbc9;
  }
`;
